import { forwardRef, useEffect, useState } from 'react'
import { formatNumber } from '@/util'
import { ModalProps } from '@/ui'
import { EngagementItemCard } from '@/layouts/engagement/component'
import { PlusContained } from '@/icons'
import { ProgressionProgramItem, ResourceState } from '@/api/dashboard'
import { SelectSkuModal } from '@/layouts/components/SelectSkuModal/SelectSkuModal'
import { Button, Input, Trash, useModal } from '@dashboard/ui'
import { useTranslation } from 'react-i18next'
import { AddButton } from '@/layouts/components/AddButton'
import { useGenerateCompanyPath } from '@/libs'
import { SKUItem } from '@/api/types'
import { dashboardClient } from '@/api'

interface EngagementItemFieldProps {
  value: ProgressionProgramItem[]
  onChange: (items: ProgressionProgramItem[]) => void
  preview?: boolean
}

export const ProgressionLevelField = forwardRef<HTMLDivElement, EngagementItemFieldProps>(
  ({ preview = true, value, onChange }, ref) => {
    const { t } = useTranslation()
    const { companyId, gameId } = useGenerateCompanyPath()
    const [skuItems, setSKUItems] = useState<SKUItem[]>([])

    useEffect(() => {
      const loadItems = async () => {
        const ids = value
          .map(it => it.item_id)
          .filter(it => !skuItems.find(sku => sku.id === it))
          .sort()
          .join(',')

        if (!ids) {
          return
        }

        const { data } = await dashboardClient.v1.getItems({
          companyId,
          gameId,
          state: ResourceState.Active,
          ids: ids,
        })
        setSKUItems(data)
      }

      loadItems()
    }, [])

    const openPicker = useModal<ModalProps>(({ ...rest }) => (
      <SelectSkuModal
        {...rest}
        title={t('select-items')}
        picked={value.map(it => skuItems.find(itemRead => itemRead.id == it.item_id)).filter(it => !!it)}
        onSubmit={items => {
          setSKUItems(items)
          rest?.onClose?.()
          const newItems = [] as ProgressionProgramItem[]

          for (const item of items) {
            const currentItem = value.find(it => it.item_id === item.id)
            if (currentItem) {
              newItems.push(currentItem)
            } else {
              newItems.push({
                item_id: item.id,
                quantity: item.is_stackable ? 1 : null,
              } as ProgressionProgramItem)
            }
          }
          onChange([...newItems])
        }}
      />
    ))

    const renderItem = (item: ProgressionProgramItem, index: number) => {
      const skuItem = skuItems.find(it => it.id === item.item_id)
      return (
        <EngagementItemCard
          key={index}
          item={skuItem || { sku: item.item_id, name: '', image_url: '', description: '' }}
          extra={
            preview &&
            skuItem?.is_stackable && (
              <span className="text-nowrap text-caption-md text-text-quarterary-hover">
                {t('daily-rewards.reward.item.quantity')}: {item.quantity}
              </span>
            )
          }
          actions={
            !preview && (
              <div className="flex items-center gap-2">
                {skuItem?.is_stackable && (
                  <div className="inline-flex min-w-[35%] items-center justify-end gap-1">
                    <span className="text-nowrap text-caption-md text-text-quarterary-hover">
                      {t('sku.item.quantity')}:
                    </span>
                    <Input
                      className="max-w-24"
                      type="number"
                      placeholder={formatNumber(item.quantity || 0)}
                      step={1}
                      value={item.quantity}
                      onChange={e => {
                        const numValue = e.target.value ? parseInt(e.target.value) : null
                        const updatedItems = value.map((it, i) =>
                          i === index ? ({ ...it, quantity: numValue } as ProgressionProgramItem) : it,
                        )
                        onChange(updatedItems)
                      }}
                    />
                  </div>
                )}
                <Button
                  variant="tertiary"
                  color="danger"
                  type="button"
                  size="md"
                  onClick={() => {
                    const updatedItems = value.filter((_, i) => i !== index)
                    onChange(updatedItems)
                  }}
                >
                  <Trash />
                </Button>
              </div>
            )
          }
        />
      )
    }

    return (
      <div ref={ref} className="flex flex-col gap-1.5">
        {skuItems.length > 0 && value.map((item, index) => renderItem(item, index))}
        {!preview && (
          <AddButton icon={PlusContained} onClick={() => openPicker()}>
            Add
          </AddButton>
        )}
      </div>
    )
  },
)
